import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Button, ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import type { IconComponent } from '@change-corgi/design-system-legacy/icons';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { useCTAButtonVariant } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { useTrackView } from 'src/app/shared/hooks/tracking';

type InsightBoxParams = {
	topChannel: {
		channel: string;
		iconColor: string;
		signaturePercent: number;
		translationKey: TranslationKey;
		icon: IconComponent;
	};
	promoteLink: string;
	handlePromoteClick: () => void;
	handleShareClick: () => Promise<void>;
};

export function InsightBox({
	topChannel,
	promoteLink,
	handlePromoteClick,
	handleShareClick,
}: InsightBoxParams): React.JSX.Element {
	const { translate } = useI18n();
	useTrackView('petition_manage_signature_insights_box_view', { topChannel: topChannel.channel });
	const { getCTAVariant } = useCTAButtonVariant();

	return (
		<Flex
			data-qa="signature-counts-insight-top-channel"
			data-testid="signature-counts-insight-message"
			backgroundColor="primary-greyBackground"
			p={24}
			sx={{
				borderRadius: '8px',
				alignItems: 'center',
				gap: 24,
				flexDirection: ['column', 'row'],
				flexWrap: 'nowrap',
			}}
		>
			<Icon icon={topChannel.icon} size={34} color={topChannel.iconColor} />
			{topChannel.channel === 'promotion' ? (
				<>
					<Text size="default">
						<Translate value="corgi.petition.starter_dashboard.petition_insights.signature_sources.insights.promote" />
					</Text>
					<ButtonLink
						data-testid="signature-source-share-petition-button"
						data-qa="signature-source-share-petition-button"
						to={promoteLink}
						onClick={handlePromoteClick}
						variant={getCTAVariant('secondaryEmphasis')}
						size="medium"
						// necessary to use style here to override min-width
						// TODO find a way to allow for this in the DS
						// eslint-disable-next-line react/forbid-component-props
						style={{
							minWidth: '200px',
							width: 'auto',
						}}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_insights.signature_sources.promote_petition_button" />
					</ButtonLink>
				</>
			) : (
				<>
					<Text size="default">
						<Translate
							value="corgi.petition.starter_dashboard.petition_insights.signature_sources.insights.share"
							replacements={{ channelName: translate(topChannel.translationKey) }}
						/>
					</Text>
					<Button
						data-testid="signature-source-share-petition-button"
						data-qa="signature-source-share-petition-button"
						variant={getCTAVariant('secondaryEmphasis')}
						size="medium"
						// necessary to use style here to override min-width
						// TODO find a way to allow for this in the DS
						// eslint-disable-next-line react/forbid-component-props
						style={{
							minWidth: '200px',
							width: 'auto',
						}}
						onClick={handleShareClick}
					>
						<Translate value="corgi.petition.starter_dashboard.petition_insights.signature_sources.share_petition_button" />
					</Button>
				</>
			)}
		</Flex>
	);
}
